import { Inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, createEffect, ofType, ROOT_EFFECTS_INIT } from '@ngrx/effects'
import { ROUTER_NAVIGATED, RouterNavigatedAction } from '@ngrx/router-store'
import { select, Store } from '@ngrx/store'
import { from, Observable, of, Subject } from 'rxjs'
import { catchError, concatMap, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators'

import { ErrorReportingService } from '../../shared/services/error-reporting.service'
import { DRR_BASE_HREF } from '../../shared/shared.config'
import { PayloadedAction } from '../../shared/types'
import { dispatchErrorAction } from '../../shared/utils'
import { Uuid } from '../../shared/utils/uuid'
import { AAAStore } from '../../store/root-reducer'
import { ConfigService } from '../config/config.service'
import { COUNTRY_CA } from '../constants/shared.constants'
import { callStatusRequest } from '../dashboard/calls-statuses/call-status.actions'
import { RAP_PROGRAM_CODES } from '../dashboard/calls.utils'
import { IssueType } from '../issue/issue.types'
import { RouteTypes } from '../main.routes'
import {
  MEMBER_INFO,
  memberInfoRequest,
  memberInfoSuccess,
  MEMBERS_SEARCH,
  requestVehicleLoad,
  searchMembersReset,
  setEligibilityVehicle,
  vehicleLoadSuccess,
} from '../member/member.actions'
import { selectIsQuickRenewResolving } from '../member/member.selectors'
import { CaptchaVersion, MEMBERSHIP_ERRORS, MemberTypesFromMessage, Vehicle } from '../member/member.types'
import { getAutomatedEventDetails, getEncodedAuthCredentials, isCaaZipCode } from '../member/member.utils'
import { startSessionTimeout } from '../session/session.actions'
import { selectUserSessionId } from '../session/session.selectors'
import { setContactNumber } from '../submit/submit.actions'
import { AdobeEventService } from '../tagging/adobe/event-adobe.service'
import { AdobeMemberValidationService } from '../tagging/adobe/member-validation-adobe.service'
import events from '../tagging/events'
import auth from '../tagging/events/auth'
import { automatedEventAction } from '../tagging/tagging.actions'
import { TaggingService } from '../tagging/tagging.service'
import { AdobeEventTypes, FormSubmitStatus, MemberValidationType } from '../tagging/tagging.types'
import { handleAuthAnalytics } from '../tagging/tagging.utils'
import { resetLoading } from '../ui/loading/loading.actions'
import { selectFirstCallStatusTimeout } from '../ui/timeout/timeout.selectors'
import {
  closeDialog,
  openErrorDialog,
  openPromptDialog,
  resetSkipIssue,
  setCaptchaModalOpen,
  setCaptchaVersion,
  setIssueType,
  setSplashscreenStep,
  setUserDefaultCoords,
} from '../ui/ui.actions'
import { selectCountry, selectQueryParamsVehicleData } from '../ui/ui.selectors'
import { UIService } from '../ui/ui.service'
import { ErrorDialogTypes, MessageDialogTypes, PromptDialogTypes } from '../ui/ui.types'
import { selectVehicleYears } from '../vehicle/vehicle.selectors'
import { isVehiclePresent, VehicleUtils } from '../vehicle/vehicle.utils'
import {
  assignEncryptedMemberNumberParams,
  assignSecureParams,
  AUTH,
  AUTH_NAME_SEARCH,
  AUTH_NAME_SEARCH_INFO,
  AUTH_NO_ROUTING,
  AUTH_OEM,
  AUTH_OEM_VALIDATION_SUCCESS,
  AUTH_PHONE_NUMBER,
  AUTH_PLATE_TO_VIN_VALIDATION,
  AUTH_RENTAL,
  AUTH_VAS,
  authNameSearchSuccess,
  authNoRouting,
  authOemSuccess,
  authOemValidationSuccess,
  authPlateToVinValidationSuccess,
  authRentalSuccess,
  authSuccess,
  authVasSuccess,
  clearOdometerLookup,
  dismissPasskeyRegistrationPrompt,
  FIRST_MATCH_FROM_SEARCH,
  hydratePasskeyDismissed,
  incrementErrorCount,
  notifyAuthFailure,
  notifyAuthNameSearchFailure,
  notifyAuthRentalFailure,
  notifyAuthVasFailure,
  notifyOemAuthFailure,
  notifyRapAuthFailure,
  notifyRentalAuthFailure,
  notifyVasAuthFailure,
  PASSKEY_REGISTER,
  passkeyAuthFailure,
  passkeyAuthPrompt,
  passkeyAuthRequest,
  passkeyAuthSuccess,
  passkeyLoginComplete,
  passkeyRegisterFailure,
  passkeyRegisterSuccess,
  passkeyRegistrationPrompt,
  REQUEST_ODOMETER_BY_PLATE,
  REQUEST_ODOMETER_BY_VIN,
  requestOdometerByPlate,
  requestOdometerByVin,
  resetErrorCount,
  SET_AUTH,
  SET_ENCRYPTED_MEMBER_NUMBER,
  SET_RAP_AUTH,
  SET_SECURE_PARAMS,
  setAuthByPlateVehicles,
  setLastSearchId,
  setOdometerFloor,
  setOdometerVehicles,
  setPasskeySupported,
  setRapAuthSuccess,
} from './auth.actions'
import {
  selectAuthErrorCount,
  selectAuthMethod,
  selectIsOdometerConfirmation,
  selectIsPasskeyAuthenticating,
  selectIsSecure,
  selectLastSearchId,
  selectModeConfiguration
} from './auth.selectors'
import { AuthService } from './auth.service'
import {
  AuthMethods,
  AuthNoRoutingResponse,
  AuthRequestParams,
  AuthResponse,
  BaseAuthRequestParams,
  MemberNumberAuthRequestParams,
  MultipleVehiclesAuthResponse,
  NameAuthRequestParams,
  PlateToVinAuthRequestParams,
  RapAuthFailure,
  RapAuthResponse,
  SearchIdPhoneNumberParams,
  VinOemAuthRequestParams,
} from './auth.types'
import { CredentialsManagementService } from './credential-management/credential-management.service'
import { ContractIdentityDataOem, ContractIdentityOem, Eligibility } from './eligibility.types'
import { isEligibilityServiceDown } from './eligibility.utils'
import { VendorConfigurationMode } from './mode-configuration.types'
import { selectShouldOfferPasskeyRegistration } from './passkey/passkey.selectors'
import { PasskeyService } from './passkey/passkey.service'

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<AAAStore>,
    private _authService: AuthService,
    private errorReportingService: ErrorReportingService,
    private router: Router,
    private taggingService: TaggingService,
    private adobeMemberValidationService: AdobeMemberValidationService,
    private adobeEventService: AdobeEventService,
    private configService: ConfigService,
    private vehicleUtils: VehicleUtils,
    private uuid: Uuid,
    private credentialsService: CredentialsManagementService,
    private uiService: UIService,
    private passkeyService: PasskeyService,
    @Inject(DRR_BASE_HREF) private drrBaseHref: string
  ) {}

  getEncodedCredentials = (
    credentials: NameAuthRequestParams | MemberNumberAuthRequestParams,
    userSessionId: string,
    errorCount: number) => getEncodedAuthCredentials(credentials, userSessionId, errorCount)

  handleEncryptedLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SET_SECURE_PARAMS),
      tap(() => {
        this.taggingService.setAutomatedEvent(
          events.auth.AAA_NATIONAL_AUTH_REQUEST,
          events.auth.PAGE_TYPE
        )
        this.adobeMemberValidationService.sendEvent(AdobeEventTypes.MEMBER_VALIDATION, MemberValidationType.CLUB_SIGN_IN)
      }),
      switchMap((action: ReturnType<typeof assignSecureParams>) =>
        this._authService.secureSignin$(action.payload).pipe(
          switchMap((data: AuthResponse) => [
            authSuccess({
              payload: {
                ...data,
                club: this.configService.getConfig().unsecureClub,
                method: AuthMethods.AAA_NATIONAL,
                isSecure: true,
              },
            }),
            resetSkipIssue(),
            memberInfoRequest(),
          ]),
          catchError((error) => {
            const { reason } = getAutomatedEventDetails(error)

            handleAuthAnalytics(this.taggingService, this.adobeMemberValidationService, {
              reason,
              method: AuthMethods.AAA_NATIONAL,
              isValid: false,
            })
            return this.errorReportingService.notifyErrorObservable(
              error,
              notifyAuthFailure
            )
          })
        )
      ),
      catchError((error) => {
        const { reason } = getAutomatedEventDetails(error)

        handleAuthAnalytics(this.taggingService, this.adobeMemberValidationService, {
          reason,
          method: AuthMethods.AAA_NATIONAL,
          isValid: false,
        })

        return this.errorReportingService.notifyErrorObservable(
          error,
          notifyAuthFailure
        )
      })
    )
  )

  handleEncryptedMemberNumberLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SET_ENCRYPTED_MEMBER_NUMBER),
      switchMap((action: ReturnType<typeof assignEncryptedMemberNumberParams>) =>
        this._authService.secureMembershipNumberSignin$(action.payload).pipe(
          switchMap((data: AuthResponse) => [
            authSuccess({
              payload: {
                ...data,
                club: this.configService.getConfig().unsecureClub,
                method: AuthMethods.ENCRYPTED_MEMBER_NUMBER,
                isSecure: true,
              },
            }),
            resetSkipIssue(),
            memberInfoRequest(),
          ]),
          catchError((error) => this.errorReportingService.notifyErrorObservable(
            error,
            notifyAuthFailure
          ))
        )
      ),
      catchError((error) => this.errorReportingService.notifyErrorObservable(
        error,
        notifyAuthFailure
      ))
    )
  )

  handleLogin$: Observable<
    | ReturnType<typeof authSuccess>
    | ReturnType<typeof memberInfoRequest>
    | ReturnType<typeof requestVehicleLoad>
    | ReturnType<typeof notifyAuthFailure>
  > = createEffect(() =>
      this.actions$.pipe(
        ofType(AUTH.REQUEST),
        withLatestFrom(
          this.store$.select(selectUserSessionId),
          this.store$.select(selectAuthErrorCount),
        ),
        switchMap(([action, userSessionId, errorCount]: [PayloadedAction, string, number]) =>
          this._authService.unsecureSignin$(action.payload).pipe(
            mergeMap((data: AuthResponse) => {
              if (action.payload.rememberMe) {
                this.credentialsService.storeCredentials({
                  memberNumber: action.payload.memberNumber,
                  zipCode: action.payload.zipCode,
                })
              }
              if (isCaaZipCode(action.payload.zipCode)) {
                this.taggingService.setAutomatedEvent(auth.CAA_ZIP_FORMAT_AUTH_NUMBER, auth.PAGE_TYPE)
              }
              window.localStorage.setItem('remember-credentials', action.payload.rememberMe)
              return this.loginSuccessActions(data, action.payload.method)
            }),
            catchError((error) => {
              const { errorEventAction } = getAutomatedEventDetails(error)
              if (errorEventAction) {
                this.taggingService.setAutomatedEvent(errorEventAction, events.auth.PAGE_TYPE)
              }
              if(error.response?.data?.searchId && error.response?.data?.phoneNumbersHint.length) {
                this.store$.dispatch(setLastSearchId({
                  payload: error.response.data.searchId
                }))
                return this.handlePhoneNumberTry(error.response.data.searchId, error.response.data.phoneNumbersHint)
              } else {
                return this.handleInvalidAuth(error, userSessionId, errorCount, action.payload)
              }
            })
          )
        ),
        catchError((error) =>
          this.errorReportingService.notifyErrorObservable(
            error,
            notifyAuthFailure
          )
        )
      )
    )

  handleSearchIdPhoneNumberAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AUTH_PHONE_NUMBER.REQUEST),
      withLatestFrom(
        this.store$.select(selectLastSearchId),
      ),
      map(([action, lastSearchId]: [PayloadedAction, string]) => ({
        searchId: lastSearchId,
        phoneNumber: action.payload,
      })),
      withLatestFrom(
        this.store$.select(selectUserSessionId),
        this.store$.select(selectAuthErrorCount),
      ),
      switchMap(([params, userSessionId, errorCount]: [SearchIdPhoneNumberParams, string, number]) =>
        this._authService.selectBySearchIdAndPhoneNumber$(params).pipe(
          mergeMap((data: AuthResponse) => [
            ...this.loginSuccessActions(data, AuthMethods.MEMBERSHIP_NUMBER),
            automatedEventAction({
              payload: {
                action: events.auth.FORM_PHONE_NUMBER_ONLY_AUTH_SUCCESS,
                pageType: events.auth.PAGE_TYPE
              }
            }),
          ]),
          catchError((e) => {
            this.taggingService.setAutomatedEvent(
              events.auth.FORM_PHONE_NUMBER_ONLY_AUTH_FAILURE,
              events.auth.PAGE_TYPE
            )
            const error = {
              ...e,
              response: {
                data: {
                  message: MemberTypesFromMessage.MEMBER_LOOKUP_ERROR
                }
              }
            }
            const payload = {
              ...params,
              method: AuthMethods.MEMBERSHIP_NUMBER
            }
            return this.handleInvalidAuth(error, userSessionId, errorCount, payload)
          })
        )
      )
    )
  )

  handleSearchIdPhoneNumberAuthFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AUTH_PHONE_NUMBER.FAILURE),
      withLatestFrom(
        this.store$.select(selectUserSessionId),
        this.store$.select(selectAuthErrorCount),
      ),
      switchMap(([_, userSessionId, errorCount]: [PayloadedAction, string, number]) => {
        const error = {
          response: {
            data: {
              message: MemberTypesFromMessage.MEMBER_LOOKUP_ERROR
            }
          }
        }
        const payload = {
          method: AuthMethods.MEMBERSHIP_NUMBER
        }
        return this.handleInvalidAuth(error, userSessionId, errorCount, payload)
      })
    )
  )

  handlePhoneNumberTry = (searchId, phoneNumbersHint) => [
    setLastSearchId({
      payload: searchId
    }),
    openPromptDialog({
      payload: {
        type: PromptDialogTypes.AUTH_PHONE_NUMBER_DIALOG,
        disableClose: true,
        params: {
          phoneNumbersHint
        }
      }
    })
  ]

  handleInvalidAuth = (error, userSessionId, errorCount, payload: AuthRequestParams | BaseAuthRequestParams) => {
    const { reason } = getAutomatedEventDetails(error)
    const encodedCredentials = this.getEncodedCredentials(payload as NameAuthRequestParams | MemberNumberAuthRequestParams, userSessionId, errorCount)

    const actions = []
    if (MEMBERSHIP_ERRORS.CAPTCHA_TOKEN.reason === reason) {
      actions.push(
        setCaptchaModalOpen({ payload: true }),
        setCaptchaVersion({ payload: CaptchaVersion.V2 })
      )
    } else {
      actions.push(dispatchErrorAction(notifyAuthFailure, error))
    }

    handleAuthAnalytics(this.taggingService, this.adobeMemberValidationService, {
      reason,
      method: payload.method,
      isValid: false,
      membershipNumber: payload.memberNumber,
    }, {
      propertyName: encodedCredentials,
      ...(error.response?.status ? { status: error.response.status } : {})
    })

    return this.errorReportingService.notifyErrorObservable(error, actions)
  }

  loginSuccessActions = (data: AuthResponse, method: AuthMethods) => [
    authSuccess({
      payload: {
        ...data,
        club: this.configService.getConfig().unsecureClub,
        method,
        isSecure: false,
      },
    }),
    resetSkipIssue(),
    memberInfoRequest(),
  ]

  handleLoginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof memberInfoSuccess>>(MEMBER_INFO.SUCCESS),
      withLatestFrom(
        this.store$.pipe(select(selectAuthMethod)),
        this.store$.pipe(select(selectIsSecure)),
        this.store$.pipe(select(selectFirstCallStatusTimeout)),
        this.store$.pipe(select(selectIsQuickRenewResolving)),
      ),
      // The quick-renew background poll (DRRWEB-3502) re-fires MEMBER_INFO.SUCCESS
      // every 30s while the payment-in-process sheet is open — only the tick that
      // actually resolves the payment should re-run session-timeout/call-status/
      // vehicle-load; every other background tick is a silent recheck.
      filter(([action, , , , resolving]) => !action.background || resolving),
      switchMap((inputs) =>
        of(inputs).pipe(
          tap(([action, authMethod]) => {
            if (action.payload.eligible && !action.payload.ersAbuser) {
              handleAuthAnalytics(this.taggingService, this.adobeMemberValidationService, {
                method: authMethod as AuthMethods,
                isValid: true,
                membershipNumber: action.payload.fullMembershipNumber,
              })
            }
          }),
          mergeMap(([action, _, isSecure, firstCallStatusTimeout]) => {
            const actions = [
              startSessionTimeout(),
              callStatusRequest({
                payload: {
                  retry: false,
                  timeout: firstCallStatusTimeout,
                  ...(action.payload.eligible !== undefined ? { eligible: action.payload.eligible } : {}),
                  ...(action.payload.ersAbuser !== undefined ? { ersAbuser: action.payload.ersAbuser } : {}),
                },
              }),
              requestVehicleLoad(),
            ]
            if (isSecure) {
              actions.push(setSplashscreenStep({ payload: 2 }))
            }
            return actions
          }),
          catchError((error) => {
            const { reason } = getAutomatedEventDetails(error)
            handleAuthAnalytics(this.taggingService, this.adobeMemberValidationService, {
              reason,
              method: inputs[1],
              isValid: false,
            })

            return this.errorReportingService.notifyErrorObservable(
              error,
              notifyAuthFailure
            )
          })
        )
      )
    )
  )

  handleLoginBySearch$ = createEffect(
    (): Observable<
      | ReturnType<typeof authNameSearchSuccess>
      | ReturnType<typeof notifyAuthFailure>
    > =>
      this.actions$.pipe(
        ofType(AUTH_NAME_SEARCH.REQUEST),
        switchMap((action: PayloadedAction) =>
          this._authService.searchSignin$(action.payload).pipe(
            concatMap((response) => [
              searchMembersReset(),
              authNameSearchSuccess({
                payload: {
                  ...response,
                  club: this.configService.getConfig().unsecureClub,
                  isSecure: false,
                },
              }),
              resetSkipIssue(),
              memberInfoRequest(),
            ]),
            catchError((error) => {
              const { reason } = getAutomatedEventDetails(error)

              handleAuthAnalytics(this.taggingService, this.adobeMemberValidationService, {
                reason,
                method: AuthMethods.MEMBER_NAME,
                isValid: false,
              })
              return this.errorReportingService.notifyErrorObservable(error, [
                openErrorDialog({
                  payload: { type: ErrorDialogTypes.MEMBER_NO_MATCHES },
                }),
                dispatchErrorAction(notifyAuthNameSearchFailure, error),
              ])
            })
          )
        )
      )
  )

  handleLoginBySearchInfo$ = createEffect(
    (): Observable<
      | ReturnType<typeof authNameSearchSuccess>
      | ReturnType<typeof notifyAuthFailure>
    > =>
      this.actions$.pipe(
        ofType(AUTH_NAME_SEARCH_INFO.REQUEST),
        switchMap((action: PayloadedAction) =>
          this._authService.searchSigninInfo$(action.payload).pipe(
            concatMap((response) => [
              searchMembersReset(),
              authNameSearchSuccess({
                payload: {
                  ...response,
                  club: this.configService.getConfig().unsecureClub,
                  isSecure: false,
                },
              }),
              resetSkipIssue(),
              memberInfoRequest(),
              closeDialog({
                payload: { type: PromptDialogTypes.ADDITIONAL_AUTH_SEARCH },
              })
            ]),
            catchError((error) => {
              const { reason } = getAutomatedEventDetails(error)

              handleAuthAnalytics(this.taggingService, this.adobeMemberValidationService, {
                reason,
                method: AuthMethods.MEMBER_NAME,
                isValid: false,
              })
              return this.errorReportingService.notifyErrorObservable(error, [
                openErrorDialog({
                  payload: { type: ErrorDialogTypes.MEMBER_NO_MATCHES },
                }),
                dispatchErrorAction(notifyAuthNameSearchFailure, error),
              ])
            })
          )
        )
      )
  )

  handleFirstMatchSearch$ = createEffect(
    (): Observable<
      | ReturnType<typeof authNameSearchSuccess>
      | ReturnType<typeof notifyAuthFailure>
    > =>
      this.actions$.pipe(
        ofType(FIRST_MATCH_FROM_SEARCH),
        switchMap((action: PayloadedAction) =>
          of(action).pipe(
            concatMap((response) => [
              searchMembersReset(),
              authNameSearchSuccess({
                payload: {
                  ...response.payload,
                  club: this.configService.getConfig().unsecureClub,
                  isSecure: false,
                },
              }),
              resetSkipIssue(),
              memberInfoRequest(),
            ]),
            catchError((error) => {
              const { reason } = getAutomatedEventDetails(error)

              handleAuthAnalytics(this.taggingService, this.adobeMemberValidationService, {
                reason,
                method: AuthMethods.MEMBER_NAME,
                isValid: false,
              })
              return this.errorReportingService.notifyErrorObservable(error, [
                openErrorDialog({
                  payload: { type: ErrorDialogTypes.MEMBER_NO_MATCHES },
                }),
                dispatchErrorAction(notifyAuthNameSearchFailure, error),
              ])
            })
          )
        )
      )
  )

  handleLoginBypass$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SET_AUTH),
      switchMap((action) =>
        of(action).pipe(
          concatMap(() => {
            this.taggingService.setAutomatedEvent(
              events.auth.AAA_MOBILE_AUTH_REQUEST,
              events.auth.PAGE_TYPE
            )
            this.adobeMemberValidationService.sendEvent(AdobeEventTypes.MEMBER_VALIDATION, MemberValidationType.APP_SIGN_IN)
            return [memberInfoRequest()]
          }),
          catchError((error) => this.errorReportingService.notifyErrorObservable(
            error,
            notifyAuthFailure
          )
          )
        )
      )
    )
  )

  // FIXME: This shouldn't be necessary. For whatever reason, route-guard.service.ts:32
  // does not detect state changes on AUTH.FAILURE, even setting ...initialState in reducer.
  // This effect has been added to mitigate the damage.
  handleLoginFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AUTH.FAILURE, AUTH_OEM.FAILURE),
        switchMap(() =>
          of([]).pipe(
            map(() => {
              this.router.navigate([this.drrBaseHref, ...RouteTypes.SIGNIN.split('/')], {
                queryParamsHandling: 'preserve',
              })
            }),
            catchError((error) =>
              this.errorReportingService.notifyErrorObservable(error, [])
            )
          )
        )
      ),
    { dispatch: false }
  )

  handleLoginFailAssistant$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AUTH.FAILURE, MEMBERS_SEARCH.FAILURE, AUTH_OEM.FAILURE, AUTH_PLATE_TO_VIN_VALIDATION.FAILURE),
        withLatestFrom(this.store$.pipe(select(selectAuthMethod))),
        filter(([action, authMethod]) =>
          (action as { payload?: RapAuthFailure }).payload !== RapAuthFailure.ELIGIBILITY_SERVICE_DOWN &&
          [AuthMethods.MEMBER_NAME, AuthMethods.MEMBERSHIP_NUMBER, AuthMethods.PLATE_TO_VIN, AuthMethods.OEM].includes(authMethod)),
        map(() => incrementErrorCount())
      ),
  )

  /**
   * For country=CA, LFA is hidden (isVinOnlyMode). Show generic MEMBER_NO_MATCHES dialog
   * on every auth failure—same message LFA shows on 3rd attempt for other countries.
   */
  handleAuthFailureGenericDialogForCA$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AUTH.FAILURE, AUTH_OEM.FAILURE, AUTH_PLATE_TO_VIN_VALIDATION.FAILURE),
        withLatestFrom(this.store$.pipe(select(selectCountry))),
        filter(([, country]) => country === COUNTRY_CA),
        map(() =>
          openErrorDialog({
            payload: { type: ErrorDialogTypes.MEMBER_NO_MATCHES },
          })
        )
      )
  )

  handleRapEligibilityServiceDown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(notifyRapAuthFailure),
      filter((action) => action.payload === RapAuthFailure.ELIGIBILITY_SERVICE_DOWN),
      map(() =>
        openErrorDialog({
          payload: { type: ErrorDialogTypes.MEMBER_NO_MATCHES },
        })
      )
    )
  )

  handleRapNotEligibleOdometerConfirmation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(notifyRapAuthFailure),
      filter((action) => action.payload === RapAuthFailure.NOT_ELIGIBLE),
      withLatestFrom(this.store$.pipe(select(selectIsOdometerConfirmation))),
      filter(([_, isOdometerConfirmation]) => Boolean(isOdometerConfirmation)),
      map(() =>
        openErrorDialog({
          payload: {
            type: ErrorDialogTypes.NOT_ELIGIBLE,
            params: {
              hideCloseButton: true,
            },
          },
        })
      )
    )
  )

  handleRapLoginBypass$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SET_RAP_AUTH.REQUEST),
      switchMap((action: PayloadedAction) => this._authService.rapVerification$(action.payload.access_token, action.payload.odometer).pipe(
        switchMap((response) => {
          this.taggingService.setAutomatedEvent(auth.FORM_RAP_TOKEN_VALIDATION_REQUEST, events.auth.PAGE_TYPE)
          const accessToken = response.eligibility.eligible ? response.access_token : action.payload.access_token
          return [
            setRapAuthSuccess(),
            authOemValidationSuccess({
              payload: {
                response: {
                  ...response,
                  access_token: accessToken,
                },
                method: AuthMethods.RAP_TOKEN,
              }
            })]
        }),
        catchError((error) => this.handleRapRequestValidationError(error, AuthMethods.RAP_TOKEN, 'Invalid Token')),
      )),
    )
  )

  handleOemAuth$ = createEffect(() => this.actions$.pipe(
    ofType(AUTH_OEM.REQUEST),
    withLatestFrom(
      this.store$.pipe(select(selectCountry)),
    ),
    switchMap(([action, country]: [PayloadedAction, string]) => {
      const params: VinOemAuthRequestParams = action.payload
      this.adobeMemberValidationService.sendEvent(AdobeEventTypes.MEMBER_VALIDATION, MemberValidationType.VIN)
      return this._authService.oemValidation$(params.captchaVersion ,params.captchaToken, params.vin, params.mileage, params.manufacturer, params.referenceId, country).pipe(
        switchMap((response) => [
          resetLoading(),
          authOemValidationSuccess({
            payload: {
              response,
              method: AuthMethods.OEM,
            }
          })]
        ),
        catchError((error) => this.handleRapRequestValidationError(error, AuthMethods.OEM, 'invalid vin number')),
      );
    })
  ))

  handlePlateToVinAuth$ = createEffect(() => this.actions$.pipe(
    ofType(AUTH_PLATE_TO_VIN_VALIDATION.REQUEST),
    withLatestFrom(
      this.store$.pipe(select(selectCountry)),
    ),
    switchMap(([action, country]: [PayloadedAction, string]) => {
      const params: PlateToVinAuthRequestParams = action.payload
      this.adobeMemberValidationService.sendEvent(AdobeEventTypes.MEMBER_VALIDATION, MemberValidationType.PLATE_TO_VIN)
      return this._authService.plateToVinValidation$(params.captchaVersion, params.captchaToken, params.plate, params.state, params.mileage, params.manufacturer, params.referenceId, country).pipe(
        switchMap((response) => {
          // The BFF answers a plate matching more than one vehicle with a 409 whose
          // customData the error interceptor emits as a success. No token comes with it:
          // the user has to pick a vehicle first, then the OEM validation issues the token.
          const vehicles = (response as MultipleVehiclesAuthResponse)?.vehicles
          if (vehicles) {
            const actions: any[] = [resetLoading()]
            actions.push(resetErrorCount())
            actions.push(setAuthByPlateVehicles({ payload: vehicles }))
            return actions
          }

          const actions: any[] = [resetLoading()]
          actions.push(authPlateToVinValidationSuccess({
            payload: {
              response: response as RapAuthResponse,
              method: AuthMethods.PLATE_TO_VIN,
            }
          }))
          return actions
        }),
        catchError((error) => this.handleRapRequestValidationError(error, AuthMethods.PLATE_TO_VIN, 'invalid license plate and state')),
      );
    })
  ))

  handleRapRequestValidationError = (error, method: AuthMethods, reason: string) => {
    const { reason: reasonMapped } = getAutomatedEventDetails(error)

    const actions = []
    actions.push(resetLoading())
    if (MEMBERSHIP_ERRORS.CAPTCHA_TOKEN.reason === reasonMapped) {
      actions.push(
        setCaptchaModalOpen({ payload: true }),
        setCaptchaVersion({ payload: CaptchaVersion.V2 })
      )
    } else {
      actions.push(notifyRapAuthFailure({ payload: method === AuthMethods.PLATE_TO_VIN ? RapAuthFailure.PLATE_TO_VIN_ERROR : RapAuthFailure.OEM_SERVER_ERROR }))
    }

    handleAuthAnalytics(this.taggingService, this.adobeMemberValidationService, {
      method,
      isValid: false,
      reason
    })
    return this.errorReportingService.notifyErrorObservable(error, actions)
  }

  handleAuthOemValidation$ = createEffect(() => this.actions$.pipe(
    ofType(AUTH_OEM_VALIDATION_SUCCESS),
    withLatestFrom(
      this.store$.pipe(select(selectModeConfiguration)),
    ),
    switchMap(([action, modeConfig]: [PayloadedAction, VendorConfigurationMode]) => {
      return this.handleRapAuthValidation(action.payload, modeConfig)
    })
  ))

  handlePlateToVinAuthSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(AUTH_PLATE_TO_VIN_VALIDATION.SUCCESS),
    withLatestFrom(
      this.store$.pipe(select(selectModeConfiguration)),
    ),
    switchMap(([action, modeConfig]: [PayloadedAction, VendorConfigurationMode]) => {
      return this.handleRapAuthValidation(action.payload, modeConfig)
    })
  ))

  /**
   * Handles RAP authentication validation logic shared between OEM and Plate-to-VIN validation
   * @param payload - The authentication payload containing response and method
   * @param modeConfig - The vendor configuration mode
   * @returns Observable of actions to dispatch
   */
  private handleRapAuthValidation = (
    payload: { response: RapAuthResponse; method: AuthMethods },
    modeConfig: VendorConfigurationMode
  ) => {
    const { response, method } = payload
    const failure = this.extractOemFailure(response.eligibility, modeConfig)

    if (failure) {
      handleAuthAnalytics(this.taggingService, this.adobeMemberValidationService, {
        method,
        isValid: false,
        reason: failure.reason
      })
      return of(notifyRapAuthFailure({ payload: failure.rapAuthFailure }))
    }

    const successActions = []
    const contractIdentity = response.eligibility.contractIdentity as ContractIdentityOem

    // Check for special case requiring tow service
    if (contractIdentity?.receivingClubCode === RAP_PROGRAM_CODES.CADDILAC_CELESTIQ) {
      successActions.push(setIssueType({
        payload: IssueType.NEED_A_TOW
      }))
    }

    // Add auth success action
    successActions.push(authOemSuccess({
      payload: {
        ...response,
        isSecure: false,
      }
    }))

    // Add coordinates if available
    if (response.latitude && response.longitude) {
      successActions.push(
        setUserDefaultCoords({
          payload: {
            lat: response.latitude,
            lng: response.longitude,
          }
        })
      )
    }

    return of(...successActions)
  }

  extractOemFailure = (eligibility: Eligibility, rapConfig: VendorConfigurationMode): {
    rapAuthFailure: RapAuthFailure,
    reason: string,
  } => {
    if(!eligibility.eligible) {
      if (isEligibilityServiceDown(eligibility)) {
        return {
          rapAuthFailure: RapAuthFailure.ELIGIBILITY_SERVICE_DOWN,
          reason: 'Eligibility API Down',
        }
      }
      return {
        rapAuthFailure: RapAuthFailure.NOT_ELIGIBLE,
        reason: 'Eligibility Fail',
      }
    }
    if(eligibility.contractIdentityData
      && this.vehicleUtils.isRestrictModel(rapConfig, (eligibility.contractIdentityData as ContractIdentityDataOem).vehicle)) {
      return {
        rapAuthFailure: RapAuthFailure.RESTRICT_MODEL,
        reason: 'Restricted Model',
      }
    }
    return null
  }

  handleOemAuthSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(AUTH_OEM.SUCCESS),
    withLatestFrom(
      this.store$.pipe(select(selectAuthMethod)),
      this.store$.pipe(select(selectQueryParamsVehicleData)),
      this.store$.pipe(select(selectModeConfiguration)),
      this.store$.pipe(select(selectVehicleYears)),
      this.store$.pipe(select(selectFirstCallStatusTimeout)),
    ),
    switchMap((action: any) => of(action).pipe(
      mergeMap(([_action, authMethod, queryParamsVehicle, modeConfig, vehicleYears, firstCallStatusTimeout]) => {
        handleAuthAnalytics(this.taggingService, this.adobeMemberValidationService, {
          method: authMethod as AuthMethods,
          isValid: _action.payload.eligibility.eligible,
        })

        const actions: any[] = [
          callStatusRequest({
            payload: {
              retry: false,
              timeout: firstCallStatusTimeout,
              eligible: _action.payload.eligibility.eligible,
            },
          }),
        ]

        const contractIdentityData = _action.payload.eligibility.contractIdentityData
        let vehicle: Vehicle = contractIdentityData?.vehicle;
        const isAllowedParamsYears = vehicleYears.includes(queryParamsVehicle?.year)
        const isAllowedParamsMake = modeConfig.makes?.find(make => make.toLowerCase() === queryParamsVehicle?.make?.toLowerCase())
        if (!isVehiclePresent(vehicle) && isAllowedParamsMake && isAllowedParamsYears) {
          vehicle = queryParamsVehicle
        }
        if (isVehiclePresent(vehicle)) {
          vehicle.id = this.uuid.getV4()
          actions.push(vehicleLoadSuccess({ payload: { vehicles: [vehicle] } }))
          actions.push(setEligibilityVehicle({ payload: vehicle }))
        }
        if (contractIdentityData?.contact?.phones?.length) {
          actions.push(setContactNumber({ payload: contractIdentityData.contact.phones[0].phoneNumber }))
        }

        actions.push(startSessionTimeout())

        return actions;
      }),
      catchError((error) => this.errorReportingService.notifyErrorObservable(error, notifyOemAuthFailure))
    )))
  )

  // RENTAL Effect
  handleRentalAuth$: Observable<
    | ReturnType<typeof authRentalSuccess>
    | ReturnType<typeof notifyAuthFailure>
    | ReturnType<typeof notifyRentalAuthFailure>
  > = createEffect(() => this.actions$.pipe(
      ofType(AUTH_RENTAL.REQUEST),
      switchMap((action: PayloadedAction) => {
        const rentalSignInRequest: (...args: any[]) => Observable<any> = this._authService.rentalValidation$.bind(this._authService)
        let resolvePostSignInActions: (authResponse: AuthResponse) => any[]
        let resolvePostSignInActionsFailure: () => any[];

        resolvePostSignInActions = (data: AuthResponse) => [
          authRentalSuccess({
            payload: {
              ...data,
              isSecure: false,
              method: AuthMethods.RENTAL
            }
          }),
        ]
        resolvePostSignInActionsFailure = () => [
          notifyAuthRentalFailure(),
        ]
        return rentalSignInRequest(action.payload.captchaVersion, action.payload.captchaToken, action.payload.refNumber, action.payload.referenceId).pipe(
          mergeMap((data: any) => {
            if (data.rentalValidation.eligibility.eligible) {
              return resolvePostSignInActions({
                ...data.rentalValidation,
                eligibility: data.rentalValidation.eligibility,
                method: action.payload.method
              })
            } else {
              return resolvePostSignInActionsFailure()
            }
          }),
          catchError((error) => {
            this.taggingService.setAutomatedEvent('invalid rental number', events.auth.PAGE_TYPE)

            handleAuthAnalytics(this.taggingService, this.adobeMemberValidationService, {
              method: action.payload.method,
              isValid: false,
            })
            return this.errorReportingService.notifyErrorObservable(
              error,
              notifyAuthFailure
            )
          }),
        );
      }),
      catchError((error) => this.errorReportingService.notifyErrorObservable(error, notifyAuthFailure))
    ))

  handleRentalAuthSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(AUTH_RENTAL.SUCCESS),
    withLatestFrom(
      this.store$.pipe(select(selectAuthMethod)),
      this.store$.pipe(select(selectFirstCallStatusTimeout)),
    ),
    switchMap((action: any) => of(action).pipe(
      mergeMap(([_action, authMethod, firstCallStatusTimeout]) => {
        handleAuthAnalytics(this.taggingService, this.adobeMemberValidationService, {
          method: authMethod as AuthMethods,
          isValid: _action.payload.eligibility.eligible,
        })

        return [
          callStatusRequest({
            payload: {
              retry: false,
              timeout: firstCallStatusTimeout,
              eligible: _action.payload.eligibility.eligible,
            },
          }),
        ]
      }),
      catchError((error) => this.errorReportingService.notifyErrorObservable(error, notifyRentalAuthFailure))
    )))
  )

  // VAS Effect
  handleVasAuth$: Observable<
    | ReturnType<typeof authVasSuccess>
    | ReturnType<typeof notifyAuthFailure>
    | ReturnType<typeof notifyVasAuthFailure>
  > = createEffect(() => this.actions$.pipe(
      ofType(AUTH_VAS.REQUEST),
      switchMap((action: PayloadedAction) => {
        const vasSignInRequest: (...args: any[]) => Observable<any> = this._authService.vasValidation$.bind(this._authService)
        let resolvePostSignInActions: (authResponse: AuthResponse) => any[]
        let resolvePostSignInActionsFailure: () => any[];

        resolvePostSignInActions = (data: AuthResponse) => [
          authVasSuccess({
            payload: {
              ...data,
              isSecure: false,
              method: AuthMethods.VAS
            }
          }),
        ]
        resolvePostSignInActionsFailure = () => [
          notifyAuthVasFailure(),
        ]
        return vasSignInRequest(action.payload.captchaToken, action.payload.subscriber, action.payload.referenceId).pipe(
          mergeMap((data: any) => {
            if (data.vasValidation.eligibility.eligible) {
              return resolvePostSignInActions({
                ...data.vasValidation,
                eligibility: data.vasValidation.eligibility,
                method: action.payload.method
              })
            } else {
              return resolvePostSignInActionsFailure()
            }
          }),
          catchError((error) => {
            this.taggingService.setAutomatedEvent('invalid vas number', events.auth.PAGE_TYPE)

            handleAuthAnalytics(this.taggingService, this.adobeMemberValidationService, {
              method: action.payload.method,
              isValid: false,
            })
            return this.errorReportingService.notifyErrorObservable(
              error,
              notifyAuthFailure
            )
          }),
        );
      }),
      catchError((error) => this.errorReportingService.notifyErrorObservable(error, notifyAuthFailure))
    ))

  handleVasAuthSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(AUTH_VAS.SUCCESS),
    withLatestFrom(
      this.store$.pipe(select(selectAuthMethod)),
      this.store$.pipe(select(selectFirstCallStatusTimeout)),
    ),
    switchMap((action: any) => of(action).pipe(
      mergeMap(([_action, authMethod, firstCallStatusTimeout]) => {
        handleAuthAnalytics(this.taggingService, this.adobeMemberValidationService, {
          method: authMethod as AuthMethods,
          isValid: _action.payload.eligibility.eligible,
        })

        return [
          callStatusRequest({
            payload: {
              retry: false,
              timeout: firstCallStatusTimeout,
              eligible: _action.payload.eligibility.eligible,
            },
          }),
        ]
      }),
      catchError((error) => this.errorReportingService.notifyErrorObservable(error, notifyVasAuthFailure))
    )))
  )

  libAuthResponse$ = new Subject<AuthNoRoutingResponse>()
  handleLibAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof authNoRouting>>(AUTH_NO_ROUTING),
      switchMap((act) =>
        of(act).pipe(
          tap((action) => {
            this.libAuthResponse$.next(action.payload)
            if (!action.payload.success) {
              this.uiService.setAllDialogsClosed()
            }
          }),
          catchError((error) => this.errorReportingService.notifyErrorObservable(
            error,
            notifyAuthFailure
          )
          )
        )
      )
    )
  , { dispatch: false })

  handleOdometerByVin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(REQUEST_ODOMETER_BY_VIN),
      switchMap((action: ReturnType<typeof requestOdometerByVin>) =>
        this._authService.getOdometerByVin(action.payload).pipe(
          map((response) => setOdometerFloor({ payload: { floor: response.odometer } })),
          catchError(() => of(clearOdometerLookup()))
        )
      )
    )
  )

  handleOdometerByPlate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(REQUEST_ODOMETER_BY_PLATE),
      switchMap((action: ReturnType<typeof requestOdometerByPlate>) =>
        this._authService.getOdometerByPlate(action.payload.state, action.payload.plate).pipe(
          map((vehicles) => {
            if (vehicles.length === 1) {
              return setOdometerFloor({
                payload: {
                  floor: vehicles[0].odometer,
                  vin: vehicles[0].vin,
                },
              })
            }
            return setOdometerVehicles({ payload: vehicles })
          }),
          catchError(() => of(clearOdometerLookup()))
        )
      )
    )
  )

  initPasskeySupported$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROOT_EFFECTS_INIT),
      switchMap(() =>
        from(this.passkeyService.isSupported()).pipe(
          map((supported) => setPasskeySupported({ payload: supported })),
          catchError(() => of(setPasskeySupported({ payload: false })))
        )
      )
    )
  )

  handlePasskeyAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(passkeyAuthRequest),
      switchMap(({ prefetchedOptions, prefetchedAssertion }) => {
        const auth$ = prefetchedAssertion
          ? this.passkeyService.verifyAuth$(prefetchedAssertion.assertion, prefetchedAssertion.challengeId)
          : prefetchedOptions
            ? this.passkeyService.assertWithOptions$(prefetchedOptions.optionsJSON, prefetchedOptions.challengeId)
            : this.passkeyService.assert$()
        return auth$.pipe(
          mergeMap((data: AuthResponse) => [
            passkeyAuthSuccess({ payload: data }),
            ...this.loginSuccessActions(data, AuthMethods.PASSKEY),
          ]),
          catchError(() => of(passkeyAuthFailure()))
        )
      })
    )
  )

  // Clears the passkey-authenticating latch once the post-verification login
  // navigates away from the sign-in screen — the point at which the passkey
  // loading card should disappear. Mirrors `hideSplashscreenForOtherRoutes$`.
  endPasskeyAuthenticatingOnNavigation$ = createEffect(() =>
    this.actions$.pipe(
      ofType<RouterNavigatedAction>(ROUTER_NAVIGATED),
      withLatestFrom(this.store$.pipe(select(selectIsPasskeyAuthenticating))),
      filter(
        ([action, authenticating]) =>
          authenticating &&
          !action.payload.routerState.url.includes(`/${RouteTypes.SIGNIN}`)
      ),
      map(() => passkeyLoginComplete())
    )
  )

  handlePasskeyRegister$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PASSKEY_REGISTER.REQUEST),
      switchMap(() =>
        this.passkeyService.register$().pipe(
          map(() => passkeyRegisterSuccess()),
          catchError(() => of(passkeyRegisterFailure()))
        )
      )
    )
  )

  hydratePasskeyDismissed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROOT_EFFECTS_INIT),
      filter(() => this.uiService.isBrowser),
      map(() => {
        try {
          const stored = sessionStorage.getItem('drr_passkey_prompt_dismissed')
          return hydratePasskeyDismissed({ payload: stored === 'true' })
        } catch {
          return hydratePasskeyDismissed({ payload: false })
        }
      })
    )
  )

  persistPasskeyDismissed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dismissPasskeyRegistrationPrompt),
      filter(() => this.uiService.isBrowser),
      tap(() => {
        try {
          sessionStorage.setItem('drr_passkey_prompt_dismissed', 'true')
        } catch {
          // no-op: sessionStorage may be unavailable
        }
      })
    )
  , { dispatch: false })

  showPasskeyRegistrationAfterOnOurWay$ = createEffect(() =>
    this.actions$.pipe(
      ofType(closeDialog),
      filter((action) => action.payload?.type === MessageDialogTypes.ON_OUR_WAY),
      withLatestFrom(this.store$.select(selectShouldOfferPasskeyRegistration)),
      filter(([, shouldOffer]) => shouldOffer),
      map(() => passkeyRegistrationPrompt())
    )
  )

  // PASSKEY AUTH TRACKING EFFECTS (4 effects)
  trackPasskeyAuthPrompt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(passkeyAuthPrompt),
      tap(() => {
        this.adobeMemberValidationService.sendEvent(
          AdobeEventTypes.MEMBER_VALIDATION_PROMPT,
          MemberValidationType.PASSKEY
        )
      })
    )
  , { dispatch: false })

  trackPasskeyAuthRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(passkeyAuthRequest),
      tap(() => {
        this.adobeMemberValidationService.sendEvent(
          AdobeEventTypes.MEMBER_VALIDATION,
          MemberValidationType.PASSKEY
        )
      })
    )
  , { dispatch: false })

  trackPasskeyAuthSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(passkeyAuthSuccess),
      tap(() => {
        this.adobeMemberValidationService.sendEvent(
          AdobeEventTypes.MEMBER_VALIDATION_RETURN,
          MemberValidationType.PASSKEY,
          { form_submit_status: FormSubmitStatus.SUCCESS }
        )
      })
    )
  , { dispatch: false })

  trackPasskeyAuthFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(passkeyAuthFailure),
      tap(() => {
        this.adobeMemberValidationService.sendEvent(
          AdobeEventTypes.MEMBER_VALIDATION_RETURN,
          MemberValidationType.PASSKEY,
          { form_submit_status: FormSubmitStatus.FAIL }
        )
      })
    )
  , { dispatch: false })

  // PASSKEY REGISTRATION TRACKING EFFECTS (5 effects)
  trackPasskeyRegistrationPrompt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(passkeyRegistrationPrompt),
      tap(() => {
        this.taggingService.setPageLoadEvent({
          pageType: events.dashboard.PAGE_TYPE,
          pageName: events.dashboard.PASSKEY_REGISTRATION_PROMPT,
        })
      })
    )
  , { dispatch: false })


  trackPasskeyRegistrationRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PASSKEY_REGISTER.REQUEST),
      tap(() => {
        this.adobeEventService.sendEvent({
          eventName: AdobeEventTypes.SYSTEM,
          eventValue: events.dashboard.PASSKEY_REGISTRATION_REQUEST,
        })
      })
    )
  , { dispatch: false })

  trackPasskeyRegistrationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PASSKEY_REGISTER.SUCCESS),
      tap(() => {
        this.adobeEventService.sendEvent({
          eventName: AdobeEventTypes.SYSTEM,
          eventValue: events.dashboard.PASSKEY_REGISTRATION_SUCCESS,
        })
      })
    )
  , { dispatch: false })

  trackPasskeyRegistrationFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PASSKEY_REGISTER.FAILURE),
      tap(() => {
        this.adobeEventService.sendEvent({
          eventName: AdobeEventTypes.SYSTEM,
          eventValue: events.dashboard.PASSKEY_REGISTRATION_FAILURE,
        })
      })
    )
  , { dispatch: false })

  trackPasskeyRegistrationDismiss$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dismissPasskeyRegistrationPrompt),
      tap(() => {
        this.adobeEventService.sendEvent({
          eventName: AdobeEventTypes.SYSTEM,
          eventValue: events.dashboard.PASSKEY_REGISTRATION_DISMISS,
        })
      })
    )
  , { dispatch: false })

}
